import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, App, Button, Form, Grid, Input, InputNumber, Space, Spin, Typography, theme } from 'antd';
import { AimOutlined, EnvironmentOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { cihazKonumuAl } from '../../utils/cihazKonumu';
import { loadGoogleMaps, mapsLink } from '../../utils/googleMaps';
import { IlIlceAlanlari } from './formParcalari';

/**
 * Haritalı adres (27.10.2026) — görüşme formu ve lokasyona dönüştürme.
 * Haritaya dokunma / işaretçiyi sürükleme / arama / haritadaki "Konumum" → Enlem, Boylam (7 ondalık) → tarayıcıda ters
 * adres (google.maps.Geocoder, yedek Nominatim; MapContainer ile aynı yol) → /konum-coz (ad + yakın lokasyonlar) →
 * il, ilçe, adres (ve mahalle) dolar; hepsi elle düzeltilebilir. Enlem / Boylam / doğruluk gizli alan.
 * SDK utils/googleMaps.js'ten (MapContainer'ın script'iyle aynı); @react-google-maps/api ikinci yükleyici olur, kullanılmaz.
 */
const { Text } = Typography;
const TURKIYE = { lat: 39.0, lng: 35.0 };
const ZAMAN_ASIMI = 4000;
const LATIN = /^[0-9A-Za-zÀ-ɏ\s.'’()/-]+$/; // Google mahalleyi bazen Arapça döndürüyor (Antalya)

const sayiMi = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const yedi = (v) => Number(Number(v).toFixed(7));
// Google bazı adlarda birleşik nokta (U+0307) döndürüyor: 'Osmangazi̇'
const temiz = (v) => (v || '').normalize('NFC').replace(/̇/g, '').replace(/\s+/g, ' ').trim();
const ulkesiz = (v) => temiz(v).replace(/,\s*(Türkiye|Turkey)$/i, '');
const zamanAsimi = (p, ms) => Promise.race([p, new Promise((ok) => { setTimeout(() => ok(null), ms); })]);

/** Google ters adres sonuçları → {il, ilce, mahalle, adres}. KKTC'de administrative_area yok, yalnız locality. */
function googleAyristir(sonuclar) {
  const bilesenler = sonuclar.flatMap((s) => s.address_components || []);
  const bilesen = (tur) => {
    const c = bilesenler.find((x) => x.types.includes(tur));
    return c ? temiz(c.long_name) : null;
  };
  const il = bilesen('administrative_area_level_1');
  const yer = bilesen('locality');
  const mahalle = bilesen('administrative_area_level_4');
  const tercih = sonuclar.find((s) => s.types.some((t) => ['street_address', 'premise', 'subpremise'].includes(t)))
    || sonuclar.find((s) => !s.types.includes('plus_code')) || sonuclar[0];
  return {
    il: il || yer,
    ilce: bilesen('administrative_area_level_2') || (il ? null : yer),
    mahalle: mahalle && LATIN.test(mahalle) ? mahalle : null,
    adres: ulkesiz(tercih?.formatted_address),
  };
}

async function googleTers(maps, lat, lng) {
  try {
    const r = await zamanAsimi(new maps.Geocoder().geocode({ location: { lat, lng }, language: 'tr' }), ZAMAN_ASIMI);
    return r?.results?.length ? googleAyristir(r.results) : null;
  } catch {
    return null; // ZERO_RESULTS / kota / anahtar hatası → Nominatim
  }
}

async function nominatimTers(lat, lng) {
  const kes = new AbortController();
  const zaman = setTimeout(() => kes.abort(), ZAMAN_ASIMI);
  try {
    const r = await fetch('https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&zoom=18'
      + `&accept-language=tr&lat=${lat}&lon=${lng}`, { signal: kes.signal });
    const d = await r.json();
    const a = d?.address;
    if (!a) return null;
    // Türkiye: province = il, town / city ('Afyonkarahisar Merkez') = ilçe; KKTC: country 'Kuzey Kıbrıs', city 'Kuzey Lefkoşa'
    const il = a.province || a.state || (a.country_code !== 'tr' ? a.country : null);
    const ilce = a.town || a.county || a.city || a.state_district || null;
    const mahalle = temiz(a.suburb || a.quarter || a.neighbourhood || a.village).replace(/\s+(Mahallesi|Mah\.?)$/i, '');
    const yol = a.road ? `${a.road}${a.house_number ? ` No:${a.house_number}` : ''}` : null;
    const yer = [a.postcode, il && ilce ? `${ilce}/${il}` : (ilce || il)].filter(Boolean).join(' ');
    return {
      il, ilce, mahalle: mahalle && LATIN.test(mahalle) ? mahalle : null,
      adres: temiz([mahalle, yol, yer].filter(Boolean).join(', ')) || ulkesiz(d.display_name),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(zaman);
  }
}

/**
 * Sırayla: arama kutusu, harita, koordinat satırı, il / ilçe, (mahalleAlan varsa) mahalle, adres.
 * Açılışta kayıtlı koordinat varsa işaretçi orada, ters adres ÇAĞRILMAZ (kayıtlı il / ilçe / adres ezilmez).
 * dogrulukAlani null ise (lokasyon formu) GPS doğruluğu yalnız gösterilir, forma yazılmaz.
 */
export function HaritaliAdres({
  form, api, iller, disabled = false, adresAlan = 'adres', mahalleAlan = null, dogrulukAlani = 'konum_dogruluk',
}) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const genis = Grid.useBreakpoint().md;
  const enlem = Form.useWatch('Enlem', form);
  const boylam = Form.useWatch('Boylam', form);
  const formDogruluk = Form.useWatch((v) => (dogrulukAlani ? v[dogrulukAlani] : null), form);
  const [yerelDogruluk, setYerelDogruluk] = useState(null);
  const dogruluk = dogrulukAlani ? formDogruluk : yerelDogruluk;
  const [durum, setDurum] = useState('yukleniyor'); // yukleniyor | hazir | hata
  const [cozuluyor, setCozuluyor] = useState(false);
  const [uyari, setUyari] = useState(null);
  const [konumAliniyor, setKonumAliniyor] = useState(false);
  const [arama, setArama] = useState('');
  const [kontrol] = useState(() => document.createElement('div')); // haritadaki "Konumum" (portal)
  const kutu = useRef(null); // harita div'i: React çocuğu yok, Google doldurur
  const aramaRef = useRef(null);
  const g = useRef({}); // {maps, harita, isaretci, daire, otomatik}
  const sira = useRef(0); // hızlı tıklamada eski ters adres / konum-coz yanıtı yok sayılır
  const ilkKonum = useRef(false); // harita bir kez kayıtlı ya da seçilen noktaya getirildi
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const koordinatVar = sayiMi(enlem) && sayiMi(boylam);
  const goster = !disabled || koordinatVar; // salt okunur ve konumsuz kayıtta harita açılmaz

  const doldur = async (lat, lng) => {
    const no = ++sira.current;
    setCozuluyor(true);
    setUyari(null);
    const ters = (g.current.maps ? await googleTers(g.current.maps, lat, lng) : null) || await nominatimTers(lat, lng);
    if (no !== sira.current) return;
    let cozum = null;
    try {
      cozum = await api.get('/konum-coz', { enlem: lat, boylam: lng, il: ters?.il || undefined, ilce: ters?.ilce || undefined });
    } catch {
      cozum = null; // il / ilçeye dokunulmaz, uyarı çıkar
    }
    if (no !== sira.current) return;
    const deger = {};
    if (cozum) Object.assign(deger, { ProvinceID: cozum.ProvinceID || null, DistrictID: cozum.DistrictID || null });
    if (ters?.adres) deger[adresAlan] = ters.adres.slice(0, 330);
    if (mahalleAlan && ters) deger[mahalleAlan] = ters.mahalle ? ters.mahalle.slice(0, 50) : null;
    form.setFieldsValue(deger);
    setCozuluyor(false);
    const eksik = [!cozum?.ProvinceID && 'il', !cozum?.DistrictID && 'ilçe', !ters?.adres && 'adres'].filter(Boolean);
    setUyari(eksik.length ? `Otomatik bulunamadı: ${eksik.join(', ')} — elle girin.` : null);
  };

  const sec = (lat, lng, dog = null) => {
    ilkKonum.current = true;
    const e = yedi(lat);
    const b = yedi(lng);
    form.setFieldsValue({ Enlem: e, Boylam: b, ...(dogrulukAlani ? { [dogrulukAlani]: dog } : {}) });
    if (!dogrulukAlani) setYerelDogruluk(dog);
    doldur(e, b);
  };

  const git = (geo) => {
    const { harita } = g.current;
    if (geo.viewport) harita.fitBounds(geo.viewport);
    else {
      harita.setCenter(geo.location);
      harita.setZoom(17);
    }
    sec(geo.location.lat(), geo.location.lng());
  };

  const adresAra = async (metin) => {
    const { maps } = g.current;
    if (!maps || !metin?.trim()) return;
    try {
      const r = await zamanAsimi(new maps.Geocoder().geocode({ address: metin, region: 'TR', language: 'tr' }), ZAMAN_ASIMI);
      if (r?.results?.[0]?.geometry) {
        git(r.results[0].geometry);
        return;
      }
    } catch {
      // ZERO_RESULTS promise'i reddeder
    }
    message.warning('Aranan yer bulunamadı.');
  };

  const yerSecildi = (yer) => {
    setArama(yer?.formatted_address || yer?.name || aramaRef.current?.input?.value || '');
    if (yer?.geometry?.location) git(yer.geometry);
    else if (yer?.name) adresAra(yer.name); // öneri seçilmeden Enter
  };

  const konumum = () => {
    setKonumAliniyor(true);
    cihazKonumuAl()
      .then((k) => {
        const { harita } = g.current;
        if (harita) {
          harita.setCenter({ lat: k.enlem, lng: k.boylam });
          harita.setZoom(17);
        }
        sec(k.enlem, k.boylam, k.konum_dogruluk);
        message.success(`Konum alındı (±${k.konum_dogruluk} m).`);
      })
      .catch((e) => message.error(e.message))
      .finally(() => setKonumAliniyor(false));
  };

  const temizle = () => {
    sira.current += 1;
    setCozuluyor(false);
    setUyari(null);
    form.setFieldsValue({ Enlem: null, Boylam: null, ...(dogrulukAlani ? { [dogrulukAlani]: null } : {}) });
    setYerelDogruluk(null);
  };

  // Google dinleyicileri bir kez kurulur; her render'ın güncel işleyicisi ref'ten çağrılır
  const secRef = useRef(sec);
  secRef.current = sec;
  const yerSecildiRef = useRef(yerSecildi);
  yerSecildiRef.current = yerSecildi;

  useEffect(() => {
    if (!goster) return undefined;
    let iptal = false;
    let oncekiPac = null;
    const dinleyiciler = [];
    loadGoogleMaps('places,geometry').then((maps) => {
      if (iptal || !kutu.current) return;
      const e0 = form.getFieldValue('Enlem');
      const b0 = form.getFieldValue('Boylam');
      const kayitli = sayiMi(e0) && sayiMi(b0);
      const harita = new maps.Map(kutu.current, {
        center: kayitli ? { lat: Number(e0), lng: Number(b0) } : TURKIYE,
        zoom: kayitli ? 16 : 6,
        gestureHandling: 'greedy',
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
      });
      ilkKonum.current = kayitli;
      g.current = { maps, harita };
      dinleyiciler.push(harita.addListener('click', (ev) => {
        if (!disabledRef.current && ev.latLng) secRef.current(ev.latLng.lat(), ev.latLng.lng());
      }));
      harita.controls[maps.ControlPosition.RIGHT_BOTTOM].push(kontrol);
      const girdi = aramaRef.current?.input;
      if (girdi && maps.places?.Autocomplete) { // legacy Autocomplete (MapContainer ile aynı)
        oncekiPac = new Set(document.querySelectorAll('.pac-container'));
        // KKTC (il 99) lokasyonları da var: Kıbrıs dahil
        const otomatik = new maps.places.Autocomplete(girdi, {
          componentRestrictions: { country: ['tr', 'cy'] }, fields: ['geometry', 'formatted_address', 'name'],
        });
        otomatik.bindTo('bounds', harita);
        dinleyiciler.push(otomatik.addListener('place_changed', () => yerSecildiRef.current(otomatik.getPlace())));
        g.current.otomatik = otomatik;
      }
      setDurum('hazir');
      // İzin zaten verilmişse (sormadan) cihaz konumuna yaklaş; işaretçi konmaz
      if (!kayitli && navigator.permissions?.query) {
        navigator.permissions.query({ name: 'geolocation' })
          .then((izin) => (izin.state === 'granted' ? cihazKonumuAl() : null))
          .then((k) => {
            if (!k || iptal || ilkKonum.current || sayiMi(form.getFieldValue('Enlem'))) return;
            harita.setCenter({ lat: k.enlem, lng: k.boylam });
            harita.setZoom(15);
          })
          .catch(() => {});
      }
    }).catch(() => {
      if (!iptal) setDurum('hata');
    });
    return () => {
      iptal = true;
      const { maps, harita, isaretci, daire, otomatik } = g.current;
      dinleyiciler.forEach((d) => d.remove());
      if (maps) [harita, isaretci, otomatik].forEach((x) => x && maps.event.clearInstanceListeners(x));
      isaretci?.setMap(null);
      daire?.setMap(null);
      // Autocomplete'in body'ye eklediği öneri kutusu modal kapanınca kalmasın
      if (oncekiPac) document.querySelectorAll('.pac-container').forEach((el) => { if (!oncekiPac.has(el)) el.remove(); });
      g.current = {};
    };
  }, [goster]); // eslint-disable-line react-hooks/exhaustive-deps

  // İşaretçi ve doğruluk dairesi formdaki koordinatı izler (tıklama, arama, Konumum, kayıttan açılış, temizle)
  useEffect(() => {
    const { maps, harita } = g.current;
    if (durum !== 'hazir' || !harita) return;
    if (!koordinatVar) {
      g.current.isaretci?.setMap(null);
      g.current.daire?.setMap(null);
      return;
    }
    const konum = { lat: Number(enlem), lng: Number(boylam) };
    if (!g.current.isaretci) {
      g.current.isaretci = new maps.Marker({ position: konum, map: harita, draggable: !disabledRef.current, title: 'Seçilen konum' });
      g.current.isaretci.addListener('dragend', (ev) => secRef.current(ev.latLng.lat(), ev.latLng.lng()));
    } else {
      g.current.isaretci.setPosition(konum);
      g.current.isaretci.setMap(harita);
    }
    if (Number(dogruluk) > 0) {
      if (!g.current.daire) {
        g.current.daire = new maps.Circle({
          clickable: false, strokeColor: token.colorPrimary, strokeOpacity: 0.6, strokeWeight: 1,
          fillColor: token.colorPrimary, fillOpacity: 0.12,
        });
      }
      g.current.daire.setCenter(konum);
      g.current.daire.setRadius(Number(dogruluk));
      g.current.daire.setMap(harita);
    } else {
      g.current.daire?.setMap(null);
    }
    if (!ilkKonum.current) { // form değerleri harita kurulduktan sonra geldiyse
      ilkKonum.current = true;
      harita.setCenter(konum);
      harita.setZoom(16);
    }
  }, [durum, enlem, boylam, dogruluk, koordinatVar, token.colorPrimary]);

  useEffect(() => {
    g.current.isaretci?.setDraggable(!disabled);
  }, [disabled, durum]);

  const kucuk = { fontSize: 12 };
  return (
    <>
      {/* Autocomplete önerileri antd Modal'ın (z-index 1000) üstünde görünsün */}
      <style>{'.pac-container { z-index: 1100; }'}</style>
      <Form.Item label="Konum" style={{ marginBottom: 12 }}>
        {durum === 'hata' && (
          <Alert type="warning" showIcon
            message={disabled ? 'Harita yüklenemedi.' : 'Harita yüklenemedi; il, ilçe ve adresi elle girin.'}
            action={disabled ? null : <Button size="small" icon={<AimOutlined />} loading={konumAliniyor} onClick={konumum}>Konumum</Button>} />
        )}
        {durum !== 'hata' && goster && (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {!disabled && (
              <Input ref={aramaRef} value={arama} onChange={(e) => setArama(e.target.value)} allowClear
                prefix={<SearchOutlined />} placeholder="Adres, yer ya da işletme ara" autoComplete="off"
                onPressEnter={(e) => {
                  e.preventDefault(); // form gönderilmesin
                  if (!g.current.otomatik) adresAra(arama); // Autocomplete varsa Enter'ı o işler
                }} />
            )}
            <div style={{ position: 'relative' }}>
              <div ref={kutu} style={{
                height: genis ? 300 : 240, width: '100%', borderRadius: token.borderRadius, overflow: 'hidden',
                border: `1px solid ${token.colorBorderSecondary}`, background: token.colorFillTertiary,
              }} />
              {durum === 'yukleniyor' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin />
                </div>
              )}
            </div>
          </Space>
        )}
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 12px' }}>
          {koordinatVar ? (
            <>
              <Text type="secondary" style={kucuk}>
                <EnvironmentOutlined /> {Number(enlem).toFixed(5)}, {Number(boylam).toFixed(5)}
                {Number(dogruluk) > 0 ? ` · GPS ±${dogruluk} m` : ''}
              </Text>
              <a href={mapsLink(enlem, boylam)} target="_blank" rel="noopener noreferrer" style={kucuk}>Google Haritalar'da aç</a>
              {!disabled && (
                <Button type="link" size="small" onClick={temizle} style={{ ...kucuk, padding: 0, height: 'auto' }}>Konumu temizle</Button>
              )}
            </>
          ) : (
            <Text type="secondary" style={kucuk}>
              {disabled ? 'Konum girilmemiş.' : 'Haritaya dokunun, işaretçiyi sürükleyin ya da Konumum ile cihaz konumunu alın.'}
            </Text>
          )}
          {cozuluyor && <Text type="secondary" style={kucuk}><LoadingOutlined /> Adres bulunuyor…</Text>}
          {!cozuluyor && uyari && <Text type="warning" style={kucuk}>{uyari}</Text>}
        </div>
      </Form.Item>
      <Form.Item name="Enlem" hidden><InputNumber /></Form.Item>
      <Form.Item name="Boylam" hidden><InputNumber /></Form.Item>
      {dogrulukAlani && <Form.Item name={dogrulukAlani} hidden><InputNumber /></Form.Item>}
      <IlIlceAlanlari form={form} api={api} iller={iller} disabled={disabled} />
      {mahalleAlan && <Form.Item name={mahalleAlan} label="Mahalle"><Input maxLength={50} /></Form.Item>}
      <Form.Item name={adresAlan} label="Adres"><Input.TextArea rows={2} maxLength={330} /></Form.Item>
      {durum === 'hazir' && !disabled && createPortal(
        <Button icon={<AimOutlined />} loading={konumAliniyor} onClick={konumum}
          style={{ margin: 10, boxShadow: token.boxShadowSecondary }}>Konumum</Button>,
        kontrol,
      )}
    </>
  );
}

export default HaritaliAdres;
